import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../../ui/card";
import { 
  FileTextIcon, 
  CalendarIcon, 
  DatabaseIcon,
} from "lucide-react";
import { PercentageVisualizer } from "./CompositionDetail";
import moment from "moment";

interface CompositionMember {
  dataset_id: number;
  dataset_name: string;
  dataset_description: string;
  sampling_type: string;
  sampling_weight: number;
  origin_dataset_id: number;
  origin_dataset_name: string;
  origin_dataset_description: string;
  request_count: number;
  major_version?: number;
  minor_version?: number;
}

interface CompositionData {
  id: number;
  name: string;
  description: string;
  major_version: number;
  minor_version: number;
  parent_composition_id?: number;
  meta: any;
  application_id?: number;
  intent_id?: number;
  intent_group_id?: number;
  date_created: number;
  base_type: string;
  members?: CompositionMember[];
  member_count: number;
  total_request_count?: number;
}

interface CompositionOverviewProps {
  composition: CompositionData;
  showDistribution?: boolean;
  compact?: boolean;
}

const CompositionOverview: React.FC<CompositionOverviewProps> = ({
  composition,
  showDistribution = true,
  compact = false,
}) => {
  // Calculate total request count across all members, or use the pre-calculated value
  const totalRequestCount = composition.members?.reduce((sum, member) => sum + member.request_count, 0) || composition.total_request_count || 0;

  // Convert members to SelectedDataset format for PercentageVisualizer
  const selectedDatasets = composition.members?.map(member => ({
    dataset: {
      id: member.origin_dataset_id,
      name: member.origin_dataset_name,
      description: member.origin_dataset_description,
      request_count: member.request_count,
    },
    percentage: Math.round((member.request_count / totalRequestCount) * 100) || 0,
    sampleType: member.sampling_type.toLowerCase() as "request" | "session",
    samplePercentage: member.sampling_weight,
  })) || [];

  return (
    <div className="space-y-8">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <FileTextIcon className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground font-gc-reg">Total Requests</p>
              </div>
              <p className="text-3xl font-gc-demi-bold text-foreground">
                {totalRequestCount.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground font-gc-reg">Date Created</p>
              </div>
              <p className="text-lg font-gc-med text-foreground">
                {moment(composition.date_created).format("MMM D, YYYY")}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <DatabaseIcon className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground font-gc-reg">Member Datasets</p>
              </div>
              <p className="text-3xl font-gc-demi-bold text-foreground">
                {composition.member_count || 0}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Composition Details */}
      <Card className="border-border">
        <CardContent className="p-6">
          <h3 className="text-lg font-gc-med text-foreground mb-4">Composition Details</h3>
          <div className="space-y-6">
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">Description</p>
              <p className="text-base font-gc-reg text-foreground whitespace-pre-wrap">
                {composition.description || "No description provided"}
              </p>
            </div>
            
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">Source</p>
              <div className="text-base font-gc-reg text-foreground">
                {composition.base_type === "intent_group" ? (
                  composition.meta?.intent_name ? (
                    <Link
                      to={`/intent-group/${composition.intent_group_id}`}
                      className="text-maitai-green hover:underline"
                    >
                      {composition.meta.intent_name}
                    </Link>
                  ) : (
                    "Intent Group"
                  )
                ) : composition.base_type === "application_intent" ? (
                  composition.meta?.application_name && composition.meta?.action_type ? (
                    <>
                      <Link
                        to={`/application/${composition.application_id}`}
                        className="text-maitai-green hover:underline"
                      >
                        {composition.meta.application_name}
                      </Link>
                      {" / "}
                      <Link
                        to={`/application/${composition.application_id}/intent/${composition.intent_id}`}
                        className="text-maitai-green hover:underline"
                      >
                        {composition.meta.action_type}
                      </Link>
                    </>
                  ) : (
                    "Application Intent"
                  )
                ) : (
                  "Unknown source"
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Distribution Chart */}
      {showDistribution && (
        <Card className="border-border">
          <CardContent className="p-6">
            <h3 className="text-lg font-gc-med text-foreground mb-4">Distribution</h3>
            <PercentageVisualizer selectedDatasets={selectedDatasets} />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CompositionOverview; 